// Prépa physique (D-073) : calculs sans écran, partagés par la vue joueuse et la vue coach.
// Les points font foi en base ; ici, une estimation pour les afficher en direct.
import { addDays, parseLocalDate } from './schedule';

export type PrepaLevel = 'min' | 'med' | 'ultra';
export type BlockKind = 'warmup' | 'core' | 'legs' | 'run' | 'recovery' | 'custom';
export interface PrepaTimer { work: number; rest: number; rounds: number }
export interface PrepaBlock {
  kind: BlockKind; name: string; min: string; med: string; ultra: string; distance?: boolean; timer?: PrepaTimer | null;
  /** Un drill du club (fractionné ou circuit, D-098) : la joueuse le lance depuis sa séance, et lit ce drill-là seulement. */
  drill_id?: string | null;
}
export interface PrepaSession { name: string; intro?: string; blocks: PrepaBlock[] }
export interface PrepaScoring { min: number; med: number; ultra: number; squad: number; post: number; distance: number; record: number }
export interface ProgramShape { starts_on: string; ends_on: string; days: number[]; week: Record<string, PrepaSession> }

export const LEVELS: [PrepaLevel, string][] = [['min', 'Minimum'], ['med', 'Medium'], ['ultra', 'Ultra']];
export const LEVEL_LABEL: Record<PrepaLevel, string> = { min: 'Minimum', med: 'Medium', ultra: 'Ultra' };
export const KIND_LABEL: Record<BlockKind, string> = { warmup: 'Échauffement', core: 'Gainage', legs: 'Jambes', run: 'Course', recovery: 'Récup', custom: 'Bloc' };
export const KIND_COLOR: Record<BlockKind, string> = { warmup: '#E0A526', core: '#6D4BC2', legs: '#2B5FB8', run: '#C2410C', recovery: '#1D7A5C', custom: '#7A6E64' };
export const DEFAULT_SCORING: PrepaScoring = { min: 10, med: 20, ultra: 30, squad: 2, post: 10, distance: 20, record: 40 };
export const DAY_SHORT = ['', 'L', 'M', 'M', 'J', 'V', 'S', 'D'];
export const DAY_LONG = ['', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

/** Jour ISO : 1 = lundi … 7 = dimanche. */
export const isoDow = (date: string) => { const d = parseLocalDate(date).getDay(); return d === 0 ? 7 : d; };
/** Le lundi de la semaine. */
export const weekStart = (date: string) => addDays(date, 1 - isoDow(date));

/** La séance prévue ce jour-là, sinon null (hors dates, jour de repos, jour sans séance). */
export function sessionOn(p: ProgramShape, date: string): PrepaSession | null {
  if (date < p.starts_on || date > p.ends_on) return null;
  const dow = isoDow(date);
  return p.days.includes(dow) ? p.week[String(dow)] ?? null : null;
}

/** Les jours de séance entre deux dates (incluses). */
export function sessionDays(p: ProgramShape, from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from < p.starts_on ? p.starts_on : from; d <= to && d <= p.ends_on; d = addDays(d, 1)) if (sessionOn(p, d)) out.push(d);
  return out;
}

/** Validable par la joueuse : de la veille au surlendemain du jour prévu (comme en base). */
export const canValidate = (date: string, today: string) => today >= addDays(date, -1) && today <= addDays(date, 2);

/** Semaine n sur N du programme. */
export function weekOf(p: ProgramShape, date: string): { n: number; total: number } {
  const w = (d: string) => Math.floor((parseLocalDate(weekStart(d)).getTime() - parseLocalDate(weekStart(p.starts_on)).getTime()) / (7 * 86400_000)) + 1;
  return { n: Math.min(Math.max(w(date), 1), w(p.ends_on)), total: w(p.ends_on) };
}

export interface PointsInput { level: PrepaLevel; partner: boolean; photo: boolean; message: boolean; km: number | null; distanceBlock: boolean; best: number | null }
/** Estimation des points (même règle qu'en base) : base × binôme, + mot et photo, + km, + record. */
export function estimatePoints(s: PrepaScoring, i: PointsInput): { total: number; formula: string } {
  const base = s[i.level];
  let total = base, formula = String(base);
  if (i.partner && i.photo) { total = base * Math.max(s.squad, 1); formula = `${base} × ${s.squad}`; }
  if (i.message && i.photo) { total += s.post; formula += ` + ${s.post}`; }
  if (i.km != null && i.km > 0 && i.distanceBlock) {
    total += s.distance; formula += ` + ${s.distance}`;
    if (i.best != null && i.km > i.best) { total += s.record; formula += ` + ${s.record}`; }
  }
  return { total, formula };
}

/** Séances d'affilée : les jours de séance passés (et aujourd'hui s'il est fait), en partant du plus récent. */
export function streak(planned: string[], done: Set<string>, today: string): number {
  let n = 0;
  for (const d of [...planned].filter(x => x <= today).sort().reverse()) {
    if (done.has(d)) n++;
    else if (d === today) continue;
    else break;
  }
  return n;
}

/** « Marie », « Marie et Zoé », « Marie, Zoé et Lily ». */
const andList = (names: string[]) => (names.length > 1 ? `${names.slice(0, -1).join(', ')} et ${names[names.length - 1]}` : names[0] ?? '');

/** La prépa sans programme en cours : la joueuse lit « Ma prépa » ; un parent, la prépa de son enfant, à son prénom
 *  (D-103 ; D-121, lot 0). `kids` : les prénoms des enfants, vide pour la joueuse elle-même. */
export function prepaEmpty(kids: string[]): { title: string; text: string } {
  if (!kids.length) return { title: 'Ma prépa', text: 'Quand ton coach publie un programme, ta séance du jour arrive ici.' };
  const who = andList(kids);
  return {
    title: `La prépa de ${who}`,
    text: kids.length > 1 ? 'Quand le coach publie un programme, leur séance du jour arrive ici.' : `Quand le coach de ${who} publie un programme, sa séance du jour arrive ici.`,
  };
}

/** La tuile « Prépa physique » de l'onglet Club : la mienne, ou celle de mon enfant, à son prénom. */
export const prepaTileSub = (kids: string[] | null) =>
  !kids ? 'Ta séance du jour' : kids.length === 1 ? `La séance du jour de ${kids[0]}` : 'Leur séance du jour';
